import { TileType, DecorType, DecorDef } from '@/config/GameConfig';
import { MapDefinition } from '@/map/MapDefs';
import { ProceduralMapParams, LayoutType, DifficultyLabel } from '@/config/DifficultyScaling';
import { SeededRandom } from '@/map/SeededRandom';
import { EnemyType } from '@/config/GameConfig';

/**
 * Procedural map generator — builds a MapDefinition from a seed + params.
 * Deterministic: same seed + params always produces the same map.
 *
 * Design rules:
 * - Paths never go straight for more than 3 tiles without turning
 * - Bases & spawns are placed in edge zones / quadrants, never dead-center
 * - Supports multiple bases and multiple spawns
 */

interface Coord { col: number; row: number }

export function generateMap(
  id: string, name: string, seed: number,
  params: ProceduralMapParams, difficulty: DifficultyLabel,
  allowedEnemies: EnemyType[],
): MapDefinition {
  const rng = new SeededRandom(seed);
  const { cols, rows } = params;
  const grid = createEmptyGrid(cols, rows);

  // 1. Place border walls
  placeBorders(grid, cols, rows);

  // 2. Place spawns and bases in zones (never dead-center)
  const spawns: Coord[] = [];
  const bases: Coord[] = [];
  placeEndpoints(rng, grid, cols, rows, params.layoutType, params.pathCount, spawns, bases);

  // 3. Carve winding paths from each spawn to nearest base (max 3 straight)
  //    Each subsequent path is repelled from existing ones to add variety.
  //    Repulsion scales with map size: larger maps = more separation.
  const paths: Coord[][] = [];
  const carvedPathTiles: Set<string> = new Set(); // all tiles used by previous paths
  const minDim = Math.min(cols, rows);
  // Repulsion radius: how far (in tiles) existing paths push new ones away
  // Bigger maps → larger radius, more paths → stronger push
  const repulsionRadius = Math.max(2, Math.floor(minDim / 4));
  const repulsionWeight = 0.6 + params.pathCount * 0.2; // stronger with more paths

  for (const spawn of spawns) {
    const target = findClosestBase(spawn, bases);
    const path = carveWindingPath(rng, grid, cols, rows, spawn, target, carvedPathTiles, repulsionRadius, repulsionWeight);
    if (path) {
      paths.push(path);
      // Record all tiles from the full (unsimplified) path carved on the grid
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          if (grid[r][c] === TileType.PATH) carvedPathTiles.add(`${c},${r}`);
        }
      }
    }
  }

  // 3b. Ensure every base has at least one path reaching it
  const connectedBases = new Set<string>();
  for (const p of paths) {
    const last = p[p.length - 1];
    connectedBases.add(`${last.col},${last.row}`);
  }
  for (const base of bases) {
    const bk = `${base.col},${base.row}`;
    if (connectedBases.has(bk)) continue;
    // Find closest spawn and carve an extra path to this orphan base
    let closestSpawn = spawns[0];
    let bestDist = Infinity;
    for (const s of spawns) {
      const d = Math.abs(s.col - base.col) + Math.abs(s.row - base.row);
      if (d < bestDist) { bestDist = d; closestSpawn = s; }
    }
    const extraPath = carveWindingPath(rng, grid, cols, rows, closestSpawn, base, carvedPathTiles, repulsionRadius, repulsionWeight);
    if (extraPath) {
      paths.push(extraPath);
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          if (grid[r][c] === TileType.PATH) carvedPathTiles.add(`${c},${r}`);
        }
      }
    }
  }

  // 4. Place placement tiles adjacent to paths
  placePlacementTiles(rng, grid, cols, rows, params.placementRatio);

  // 4b. Guarantee at least 1 fixed placement tile within baseDeployRange of each base
  ensurePlacementNearBases(grid, cols, rows, bases);

  // 5. Add wall clusters
  placeWallClusters(rng, grid, cols, rows, params.wallDensity);

  // 6. Place terrain hazards (MUD, WATER, GRILLE, MAGMA) — scaled by difficulty
  const diffTier = difficultyTier(difficulty);
  placeMudPatches(rng, grid, cols, rows, diffTier);
  placeWaterBodies(rng, grid, cols, rows, diffTier);
  placeGrilleTiles(rng, grid, cols, rows, diffTier);
  placeMagmaTiles(rng, grid, cols, rows, diffTier);

  // 7. Validate
  const validPaths = paths.filter(p => p.length >= 2);
  if (validPaths.length === 0) {
    return generateFallback(id, name, cols, rows, difficulty, allowedEnemies);
  }

  // 8. Place decorations on eligible tiles (expanded pools)
  const decorations = placeDecorations(rng, grid, cols, rows, validPaths, diffTier);

  return {
    id, name, difficulty,
    description: `${cols}x${rows} ${params.layoutType}`,
    cols, rows, grid,
    paths: validPaths,
    allowedEnemies,
    decorations,
  };
}

// ── Grid initialization ──

function createEmptyGrid(cols: number, rows: number): TileType[][] {
  const grid: TileType[][] = [];
  for (let r = 0; r < rows; r++) {
    grid[r] = new Array(cols).fill(TileType.GROUND);
  }
  return grid;
}

function placeBorders(grid: TileType[][], cols: number, rows: number): void {
  for (let c = 0; c < cols; c++) {
    grid[0][c] = TileType.WALL;
    grid[rows - 1][c] = TileType.WALL;
  }
  for (let r = 0; r < rows; r++) {
    grid[r][0] = TileType.WALL;
    grid[r][cols - 1] = TileType.WALL;
  }
}

// ══════════════════════════════════════════
//  ENDPOINT PLACEMENT — zone-based, never centered
// ══════════════════════════════════════════

/** Map zones: edges and quadrants (never the dead center) */
type Zone = 'top-edge' | 'bottom-edge' | 'left-edge' | 'right-edge'
  | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

const OPPOSITE_ZONES: Record<Zone, Zone[]> = {
  'left-edge':    ['right-edge', 'top-right', 'bottom-right'],
  'right-edge':   ['left-edge', 'top-left', 'bottom-left'],
  'top-edge':     ['bottom-edge', 'bottom-left', 'bottom-right'],
  'bottom-edge':  ['top-edge', 'top-left', 'top-right'],
  'top-left':     ['bottom-right', 'right-edge', 'bottom-edge'],
  'top-right':    ['bottom-left', 'left-edge', 'bottom-edge'],
  'bottom-left':  ['top-right', 'right-edge', 'top-edge'],
  'bottom-right': ['top-left', 'left-edge', 'top-edge'],
};

/** Get a random coordinate within a zone */
function zoneCoord(rng: SeededRandom, cols: number, rows: number, zone: Zone): Coord {
  const qw = Math.floor(cols / 4);
  const qh = Math.floor(rows / 4);
  const midC = Math.floor(cols / 2);
  const midR = Math.floor(rows / 2);

  switch (zone) {
    case 'left-edge':    return { col: 0, row: rng.int(1, rows - 2) };
    case 'right-edge':   return { col: cols - 1, row: rng.int(1, rows - 2) };
    case 'top-edge':     return { col: rng.int(1, cols - 2), row: 0 };
    case 'bottom-edge':  return { col: rng.int(1, cols - 2), row: rows - 1 };
    case 'top-left':     return { col: rng.int(1, qw + 1), row: rng.int(1, qh + 1) };
    case 'top-right':    return { col: rng.int(cols - qw - 2, cols - 2), row: rng.int(1, qh + 1) };
    case 'bottom-left':  return { col: rng.int(1, qw + 1), row: rng.int(rows - qh - 2, rows - 2) };
    case 'bottom-right': return { col: rng.int(cols - qw - 2, cols - 2), row: rng.int(rows - qh - 2, rows - 2) };
  }
}

/** Pick N coords near a reference point (cluster) or spread across a zone */
function clusterCoords(
  rng: SeededRandom, cols: number, rows: number,
  zone: Zone, count: number, spread: number,
): Coord[] {
  if (count === 1) return [zoneCoord(rng, cols, rows, zone)];

  const anchor = zoneCoord(rng, cols, rows, zone);
  const coords: Coord[] = [anchor];

  for (let i = 1; i < count; i++) {
    const offsetC = rng.int(-spread, spread);
    const offsetR = rng.int(-spread, spread);
    const c = Math.max(0, Math.min(cols - 1, anchor.col + offsetC));
    const r = Math.max(0, Math.min(rows - 1, anchor.row + offsetR));
    // Avoid duplicate positions
    if (!coords.some(p => p.col === c && p.row === r)) {
      coords.push({ col: c, row: r });
    } else {
      // Fallback: random in zone
      coords.push(zoneCoord(rng, cols, rows, zone));
    }
  }
  return coords;
}

function placeEndpoints(
  rng: SeededRandom, grid: TileType[][], cols: number, rows: number,
  layout: LayoutType, pathCount: number,
  spawns: Coord[], bases: Coord[],
): void {
  const allZones: Zone[] = [
    'top-edge', 'bottom-edge', 'left-edge', 'right-edge',
    'top-left', 'top-right', 'bottom-left', 'bottom-right',
  ];

  // Decide how many bases and spawns
  // pathCount determines total endpoints; split between bases and spawns
  let baseCount: number;
  let spawnCount: number;

  switch (layout) {
    case 'horizontal':
      // Classic: spawns left, bases right
      spawnCount = pathCount;
      baseCount = Math.max(1, Math.ceil(pathCount / 2));
      placeInZones(rng, grid, cols, rows,
        ['left-edge'], spawnCount, Math.floor(rows / 4),
        ['right-edge'], baseCount, Math.floor(rows / 4),
        spawns, bases);
      return;

    case 'vertical':
      spawnCount = pathCount;
      baseCount = Math.max(1, Math.ceil(pathCount / 2));
      placeInZones(rng, grid, cols, rows,
        ['top-edge'], spawnCount, Math.floor(cols / 4),
        ['bottom-edge'], baseCount, Math.floor(cols / 4),
        spawns, bases);
      return;

    case 'central_base':
      // Bases in a random quadrant (NOT center), spawns on opposite edges
      baseCount = rng.int(1, 2);
      spawnCount = pathCount;
      {
        const quadrants: Zone[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
        const baseZone = rng.pick(quadrants);
        const spawnZones = OPPOSITE_ZONES[baseZone];
        placeInZones(rng, grid, cols, rows,
          spawnZones, spawnCount, 3,
          [baseZone], baseCount, 2,
          spawns, bases);
      }
      return;

    case 'multi_spawn':
      // Multiple spawns spread around, bases in one zone
      baseCount = rng.int(1, 2);
      spawnCount = pathCount;
      {
        const baseZone = rng.pick(allZones.filter(z => z.includes('edge'))) as Zone;
        const spawnZones = OPPOSITE_ZONES[baseZone];
        // Spread spawns across multiple opposite zones
        const usedSpawnZones = spawnZones.slice(0, Math.min(spawnZones.length, spawnCount));
        placeInZones(rng, grid, cols, rows,
          usedSpawnZones, spawnCount, 3,
          [baseZone], baseCount, 3,
          spawns, bases);
      }
      return;

    case 'spiral':
      // One spawn corner, base in opposite quadrant
      {
        const corners: Zone[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
        const spawnCorner = rng.pick(corners);
        const baseZones = OPPOSITE_ZONES[spawnCorner];
        placeInZones(rng, grid, cols, rows,
          [spawnCorner], 1, 2,
          [baseZones[0]], 1, 2,
          spawns, bases);
      }
      return;

    case 'cross':
      // Spawns on 2+ edges, bases in quadrants
      spawnCount = Math.max(2, pathCount);
      baseCount = rng.int(1, 2);
      {
        const edgeZones: Zone[] = ['left-edge', 'right-edge', 'top-edge', 'bottom-edge'];
        rng.shuffle(edgeZones);
        const spawnEdges = edgeZones.slice(0, Math.min(spawnCount, edgeZones.length));
        const quadrants: Zone[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
        const baseZone = rng.pick(quadrants);
        placeInZones(rng, grid, cols, rows,
          spawnEdges, spawnCount, 3,
          [baseZone], baseCount, 2,
          spawns, bases);
      }
      return;

    default:
      // Fallback: horizontal
      placeInZones(rng, grid, cols, rows,
        ['left-edge'], pathCount, Math.floor(rows / 4),
        ['right-edge'], 1, 2,
        spawns, bases);
  }
}

/** Place spawns and bases in their respective zones */
function placeInZones(
  rng: SeededRandom, grid: TileType[][], cols: number, rows: number,
  spawnZones: Zone[], spawnCount: number, spawnSpread: number,
  baseZones: Zone[], baseCount: number, baseSpread: number,
  spawns: Coord[], bases: Coord[],
): void {
  // Distribute spawns across their zones
  for (let i = 0; i < spawnCount; i++) {
    const zone = spawnZones[i % spawnZones.length];
    const coord = zoneCoord(rng, cols, rows, zone);
    // Ensure not already occupied
    if (grid[coord.row][coord.col] === TileType.SPAWN || grid[coord.row][coord.col] === TileType.BASE) {
      // Nudge
      coord.col = Math.max(0, Math.min(cols - 1, coord.col + rng.int(-2, 2)));
      coord.row = Math.max(0, Math.min(rows - 1, coord.row + rng.int(-2, 2)));
    }
    grid[coord.row][coord.col] = TileType.SPAWN;
    spawns.push(coord);
  }

  // Place bases — clustered or spread depending on baseCount
  const baseCoords = clusterCoords(rng, cols, rows, baseZones[0], baseCount, baseSpread);
  for (const coord of baseCoords) {
    if (grid[coord.row][coord.col] === TileType.SPAWN) {
      // Nudge away from spawn
      coord.col = Math.max(1, Math.min(cols - 2, coord.col + rng.int(1, 3)));
      coord.row = Math.max(1, Math.min(rows - 2, coord.row + rng.int(1, 3)));
    }
    grid[coord.row][coord.col] = TileType.BASE;
    bases.push(coord);
  }
}

// ══════════════════════════════════════════
//  PATH CARVING — winding, max 3 tiles straight
// ══════════════════════════════════════════

function findClosestBase(spawn: Coord, bases: Coord[]): Coord {
  let best = bases[0];
  let bestDist = Infinity;
  for (const b of bases) {
    const d = Math.abs(b.col - spawn.col) + Math.abs(b.row - spawn.row);
    if (d < bestDist) { bestDist = d; best = b; }
  }
  return best;
}

const MAX_STRAIGHT = 3; // force a turn after this many consecutive same-direction steps

/**
 * Carve a winding path from spawn to target.
 * Uses weighted random walk biased toward target, with forced turns
 * every MAX_STRAIGHT steps to avoid boring straight corridors.
 * Applies repulsion from existing paths so multi-path maps have variety.
 */
function carveWindingPath(
  rng: SeededRandom, grid: TileType[][], cols: number, rows: number,
  spawn: Coord, target: Coord,
  existingPathTiles: Set<string>, repulsionRadius: number, repulsionWeight: number,
): Coord[] | null {
  const key = (c: number, r: number) => `${c},${r}`;
  const visited = new Set<string>();
  const parent = new Map<string, string>();
  const dirInfo = new Map<string, { dc: number; dr: number; straight: number }>();

  const dirs = [
    { dc: 0, dr: -1 }, { dc: 1, dr: 0 },
    { dc: 0, dr: 1 }, { dc: -1, dr: 0 },
  ];

  // Pre-compute distance-to-nearest-existing-path for repulsion (only if paths exist)
  // distMap[r][c] = min distance to any existing path tile (capped at repulsionRadius)
  let distMap: number[][] | null = null;
  if (existingPathTiles.size > 0) {
    distMap = [];
    for (let r = 0; r < rows; r++) {
      distMap[r] = new Array(cols).fill(repulsionRadius + 1);
    }
    // BFS flood from all existing path tiles
    const bfsQueue: { c: number; r: number; d: number }[] = [];
    for (const pk of existingPathTiles) {
      const [pc, pr] = pk.split(',').map(Number);
      if (pr >= 0 && pr < rows && pc >= 0 && pc < cols) {
        distMap[pr][pc] = 0;
        bfsQueue.push({ c: pc, r: pr, d: 0 });
      }
    }
    let qi = 0;
    while (qi < bfsQueue.length) {
      const { c, r, d } = bfsQueue[qi++];
      if (d >= repulsionRadius) continue;
      for (const [dc, dr] of [[0, -1], [1, 0], [0, 1], [-1, 0]]) {
        const nc = c + dc, nr = r + dr;
        if (nc >= 0 && nc < cols && nr >= 0 && nr < rows && distMap![nr][nc] > d + 1) {
          distMap![nr][nc] = d + 1;
          bfsQueue.push({ c: nc, r: nr, d: d + 1 });
        }
      }
    }
  }

  // Priority queue (simple sorted array — maps are small enough)
  interface QueueEntry { col: number; row: number; cost: number }
  const queue: QueueEntry[] = [{ col: spawn.col, row: spawn.row, cost: 0 }];
  const costSoFar = new Map<string, number>();

  const sk = key(spawn.col, spawn.row);
  visited.add(sk);
  costSoFar.set(sk, 0);
  dirInfo.set(sk, { dc: 0, dr: 0, straight: 0 });

  while (queue.length > 0) {
    queue.sort((a, b) => a.cost - b.cost);
    const curr = queue.shift()!;
    const ck = key(curr.col, curr.row);

    if (curr.col === target.col && curr.row === target.row) {
      const path: Coord[] = [];
      let k: string | undefined = ck;
      while (k) {
        const [c, r] = k.split(',').map(Number);
        path.unshift({ col: c, row: r });
        k = parent.get(k);
      }
      for (const p of path) {
        const t = grid[p.row][p.col];
        if (t !== TileType.SPAWN && t !== TileType.BASE) {
          grid[p.row][p.col] = TileType.PATH;
        }
      }
      return simplifyPath(path);
    }

    const info = dirInfo.get(ck) ?? { dc: 0, dr: 0, straight: 0 };
    const baseCost = costSoFar.get(ck) ?? 0;

    const shuffled = [...dirs];
    rng.shuffle(shuffled);

    for (const d of shuffled) {
      const nc = curr.col + d.dc;
      const nr = curr.row + d.dr;
      if (nc < 0 || nc >= cols || nr < 0 || nr >= rows) continue;

      const tile = grid[nr][nc];
      if ((tile === TileType.WALL || tile === TileType.WATER)
          && !(nc === target.col && nr === target.row)) continue;

      const nk = key(nc, nr);

      const sameDir = (d.dc === info.dc && d.dr === info.dr);
      const newStraight = sameDir ? info.straight + 1 : 1;

      if (sameDir && info.straight >= MAX_STRAIGHT) continue;

      // Movement cost
      let moveCost = 1;
      moveCost += rng.next() * 0.8;
      if (sameDir && newStraight >= 2) moveCost += newStraight * 0.6;
      if (!sameDir && info.straight > 0) moveCost -= 0.3;

      // PATH REPULSION: penalize tiles close to existing paths
      // Tiles ON an existing path get max penalty; tiles near get scaled penalty
      if (distMap) {
        const dist = distMap[nr][nc];
        if (dist <= repulsionRadius) {
          // Inverse: closer = higher penalty. dist=0 → full weight, dist=radius → 0
          const proximity = 1 - dist / (repulsionRadius + 1);
          moveCost += proximity * repulsionWeight * 3;
        }
      }

      const newCost = baseCost + moveCost;
      const prevCost = costSoFar.get(nk);

      if (prevCost === undefined || newCost < prevCost) {
        costSoFar.set(nk, newCost);
        parent.set(nk, ck);
        dirInfo.set(nk, { dc: d.dc, dr: d.dr, straight: newStraight });
        const h = Math.abs(nc - target.col) + Math.abs(nr - target.row);
        queue.push({ col: nc, row: nr, cost: newCost + h * 1.1 });
        visited.add(nk);
      }
    }
  }

  // Fallback: try basic BFS if winding path failed
  return carveBasicPath(rng, grid, cols, rows, spawn, target);
}

/** Fallback BFS path (no straight-line constraint) — used only if winding path fails */
function carveBasicPath(
  rng: SeededRandom, grid: TileType[][], cols: number, rows: number,
  spawn: Coord, target: Coord,
): Coord[] | null {
  const key = (c: number, r: number) => `${c},${r}`;
  const visited = new Set<string>();
  const parentMap = new Map<string, string>();
  const queue: Coord[] = [spawn];
  visited.add(key(spawn.col, spawn.row));

  const dirs = [
    { dc: 0, dr: -1 }, { dc: 1, dr: 0 },
    { dc: 0, dr: 1 }, { dc: -1, dr: 0 },
  ];

  while (queue.length > 0) {
    const curr = queue.shift()!;
    const ck = key(curr.col, curr.row);

    if (curr.col === target.col && curr.row === target.row) {
      const path: Coord[] = [];
      let k: string | undefined = ck;
      while (k) {
        const [c, r] = k.split(',').map(Number);
        path.unshift({ col: c, row: r });
        k = parentMap.get(k);
      }
      for (const p of path) {
        const t = grid[p.row][p.col];
        if (t !== TileType.SPAWN && t !== TileType.BASE) {
          grid[p.row][p.col] = TileType.PATH;
        }
      }
      return simplifyPath(path);
    }

    const shuffled = [...dirs];
    rng.shuffle(shuffled);
    for (const d of shuffled) {
      const nc = curr.col + d.dc;
      const nr = curr.row + d.dr;
      if (nc < 0 || nc >= cols || nr < 0 || nr >= rows) continue;
      const nk = key(nc, nr);
      if (visited.has(nk)) continue;
      const tile = grid[nr][nc];
      if ((tile === TileType.WALL || tile === TileType.WATER)
          && !(nc === target.col && nr === target.row)) continue;
      visited.add(nk);
      parentMap.set(nk, ck);
      queue.push({ col: nc, row: nr });
    }
  }
  return null;
}

/** Remove collinear waypoints (keep only turns) */
function simplifyPath(path: Coord[]): Coord[] {
  if (path.length <= 2) return path;
  const result: Coord[] = [path[0]];
  for (let i = 1; i < path.length - 1; i++) {
    const prev = path[i - 1];
    const curr = path[i];
    const next = path[i + 1];
    if (curr.col - prev.col !== next.col - curr.col ||
        curr.row - prev.row !== next.row - curr.row) {
      result.push(curr);
    }
  }
  result.push(path[path.length - 1]);
  return result;
}

// ── Placement tiles ──

function placePlacementTiles(
  rng: SeededRandom, grid: TileType[][], cols: number, rows: number,
  ratio: number,
): void {
  const candidates: Coord[] = [];
  for (let r = 1; r < rows - 1; r++) {
    for (let c = 1; c < cols - 1; c++) {
      if (grid[r][c] !== TileType.GROUND) continue;
      if (hasAdjacentType(grid, cols, rows, c, r, TileType.PATH) ||
          hasAdjacentType(grid, cols, rows, c, r, TileType.BASE) ||
          hasAdjacentType(grid, cols, rows, c, r, TileType.SPAWN)) {
        candidates.push({ col: c, row: r });
      }
    }
  }
  rng.shuffle(candidates);
  const count = Math.max(4, Math.floor(candidates.length * ratio));
  for (let i = 0; i < Math.min(count, candidates.length); i++) {
    grid[candidates[i].row][candidates[i].col] = TileType.PLACEMENT;
  }

  // Second pass: placement tiles 2 tiles from path
  const secondPass: Coord[] = [];
  for (let r = 1; r < rows - 1; r++) {
    for (let c = 1; c < cols - 1; c++) {
      if (grid[r][c] !== TileType.GROUND) continue;
      if (hasAdjacentType(grid, cols, rows, c, r, TileType.PLACEMENT)) {
        secondPass.push({ col: c, row: r });
      }
    }
  }
  rng.shuffle(secondPass);
  const count2 = Math.floor(secondPass.length * ratio * 0.5);
  for (let i = 0; i < Math.min(count2, secondPass.length); i++) {
    grid[secondPass[i].row][secondPass[i].col] = TileType.PLACEMENT;
  }
}

/** Default deploy range in tiles — mirrors GOD.baseDeployRange default */
const BASE_DEPLOY_RANGE = 3;

/**
 * Guarantee at least 1 PLACEMENT tile within BASE_DEPLOY_RANGE of each base.
 * If none exists, convert the closest GROUND tile to PLACEMENT.
 */
function ensurePlacementNearBases(
  grid: TileType[][], cols: number, rows: number,
  bases: Coord[],
): void {
  const range = BASE_DEPLOY_RANGE;

  for (const base of bases) {
    let hasFixed = false;
    for (let dr = -range; dr <= range && !hasFixed; dr++) {
      for (let dc = -range; dc <= range && !hasFixed; dc++) {
        const r = base.row + dr;
        const c = base.col + dc;
        if (r < 0 || r >= rows || c < 0 || c >= cols) continue;
        if (Math.abs(dr) + Math.abs(dc) > range) continue;
        const tile = grid[r][c];
        if (tile === TileType.PLACEMENT || tile === TileType.GRILLE || tile === TileType.BROKEN_PLACEMENT) {
          hasFixed = true;
        }
      }
    }

    if (hasFixed) continue;

    // No fixed placement near this base — convert closest GROUND tile
    let bestDist = Infinity;
    let bestCoord: Coord | null = null;
    for (let dr = -range; dr <= range; dr++) {
      for (let dc = -range; dc <= range; dc++) {
        const r = base.row + dr;
        const c = base.col + dc;
        if (r < 1 || r >= rows - 1 || c < 1 || c >= cols - 1) continue;
        const dist = Math.abs(dr) + Math.abs(dc);
        if (dist > range) continue;
        if (grid[r][c] === TileType.GROUND && dist < bestDist) {
          bestDist = dist;
          bestCoord = { col: c, row: r };
        }
      }
    }
    if (bestCoord) {
      grid[bestCoord.row][bestCoord.col] = TileType.PLACEMENT;
    }
  }
}

function hasAdjacentType(
  grid: TileType[][], cols: number, rows: number,
  c: number, r: number, type: TileType,
): boolean {
  for (const [dc, dr] of [[0, -1], [1, 0], [0, 1], [-1, 0]]) {
    const nc = c + dc;
    const nr = r + dr;
    if (nc >= 0 && nc < cols && nr >= 0 && nr < rows && grid[nr][nc] === type) {
      return true;
    }
  }
  return false;
}

// ── Wall clusters ──

function placeWallClusters(
  rng: SeededRandom, grid: TileType[][], cols: number, rows: number,
  density: number,
): void {
  const targetWalls = Math.floor(cols * rows * density);
  let placed = 0;
  let attempts = 0;
  const maxAttempts = targetWalls * 5;

  while (placed < targetWalls && attempts < maxAttempts) {
    attempts++;
    const w = rng.int(1, 3);
    const h = rng.int(1, 3);
    const sc = rng.int(2, cols - 3);
    const sr = rng.int(2, rows - 3);

    let valid = true;
    for (let dr = 0; dr < h && valid; dr++) {
      for (let dc = 0; dc < w && valid; dc++) {
        const c = sc + dc;
        const r = sr + dr;
        if (c >= cols - 1 || r >= rows - 1) { valid = false; break; }
        if (grid[r][c] !== TileType.GROUND) valid = false;
      }
    }

    if (valid) {
      for (let dr = 0; dr < h; dr++) {
        for (let dc = 0; dc < w; dc++) {
          grid[sr + dr][sc + dc] = TileType.WALL;
          placed++;
        }
      }
    }
  }
}

// ── Difficulty tier mapping ──

const DIFFICULTY_TIER_MAP: Record<string, number> = {
  'Beginner': 0, 'Easy': 1, 'Normal': 2, 'Moderate': 3, 'Challenging': 4,
  'Hard': 5, 'Very Hard': 6, 'Expert': 7, 'Nightmare': 8, 'Infernal': 9,
};

function difficultyTier(difficulty: DifficultyLabel): number {
  return DIFFICULTY_TIER_MAP[difficulty] ?? 2;
}

// ── MUD patches — slowing zones near paths, appear from Beginner+ ──

function placeMudPatches(
  rng: SeededRandom, grid: TileType[][], cols: number, rows: number,
  tier: number,
): void {
  // Mud appears from Beginner (tier 0) — small patches that grow with difficulty
  const patchCount = Math.floor(1 + tier * 0.8);
  const patchSize = Math.min(2 + Math.floor(tier / 2), 5);

  for (let p = 0; p < patchCount; p++) {
    // Find a GROUND tile near a PATH tile as seed
    const seed = findGroundNear(rng, grid, cols, rows, TileType.PATH, 50);
    if (!seed) continue;
    // Grow an organic patch via random walk
    growPatch(rng, grid, cols, rows, seed, TileType.MUD, patchSize);
  }
}

// ── WATER bodies — blocking terrain, small ponds, appear from Easy+ ──

function placeWaterBodies(
  rng: SeededRandom, grid: TileType[][], cols: number, rows: number,
  tier: number,
): void {
  if (tier < 1) return; // no water in Beginner
  const bodyCount = Math.floor(1 + tier * 0.5);
  const bodySize = Math.min(2 + Math.floor(tier / 3), 5);

  for (let b = 0; b < bodyCount; b++) {
    // Water appears away from paths to not block them
    const seed = findGroundAwayFrom(rng, grid, cols, rows, TileType.PATH, 2, 80);
    if (!seed) continue;
    growPatch(rng, grid, cols, rows, seed, TileType.WATER, bodySize);
  }
}

// ── GRILLE tiles — industrial zones near placements, appear from Moderate+ ──

function placeGrilleTiles(
  rng: SeededRandom, grid: TileType[][], cols: number, rows: number,
  tier: number,
): void {
  if (tier < 3) return;
  const count = Math.floor(2 + (tier - 3) * 0.8);
  let placed = 0;
  let attempts = 0;

  while (placed < count && attempts < count * 10) {
    attempts++;
    const c = rng.int(1, cols - 2);
    const r = rng.int(1, rows - 2);
    if (grid[r][c] !== TileType.GROUND) continue;
    // Must be near a PLACEMENT tile
    if (!hasAdjacentType(grid, cols, rows, c, r, TileType.PLACEMENT)) continue;
    grid[r][c] = TileType.GRILLE;
    placed++;
  }
}

// ── MAGMA tiles — damage hazard, appear from Challenging+ ──

function placeMagmaTiles(
  rng: SeededRandom, grid: TileType[][], cols: number, rows: number,
  tier: number,
): void {
  if (tier < 4) return; // Challenging+
  const patchCount = Math.floor(1 + (tier - 4) * 0.6);
  const patchSize = Math.min(1 + Math.floor((tier - 3) / 2), 4);

  for (let p = 0; p < patchCount; p++) {
    const seed = findGroundNear(rng, grid, cols, rows, TileType.PATH, 60);
    if (!seed) continue;
    growPatch(rng, grid, cols, rows, seed, TileType.MAGMA, patchSize);
  }
}

// ── Patch growth helpers ──

/** Find a random GROUND tile adjacent to a given tile type */
function findGroundNear(
  rng: SeededRandom, grid: TileType[][], cols: number, rows: number,
  nearType: TileType, maxAttempts: number,
): Coord | null {
  for (let i = 0; i < maxAttempts; i++) {
    const c = rng.int(1, cols - 2);
    const r = rng.int(1, rows - 2);
    if (grid[r][c] !== TileType.GROUND) continue;
    if (hasAdjacentType(grid, cols, rows, c, r, nearType)) return { col: c, row: r };
  }
  return null;
}

/** Find a random GROUND tile that is NOT adjacent to a given tile type (min distance) */
function findGroundAwayFrom(
  rng: SeededRandom, grid: TileType[][], cols: number, rows: number,
  awayFrom: TileType, minDist: number, maxAttempts: number,
): Coord | null {
  for (let i = 0; i < maxAttempts; i++) {
    const c = rng.int(1, cols - 2);
    const r = rng.int(1, rows - 2);
    if (grid[r][c] !== TileType.GROUND) continue;
    // Check no tile of awayFrom type within minDist manhattan distance
    let tooClose = false;
    for (let dr = -minDist; dr <= minDist && !tooClose; dr++) {
      for (let dc = -minDist; dc <= minDist && !tooClose; dc++) {
        const nr = r + dr, nc = c + dc;
        if (nr >= 0 && nr < rows && nc >= 0 && nc < cols) {
          if (grid[nr][nc] === awayFrom) tooClose = true;
        }
      }
    }
    if (!tooClose) return { col: c, row: r };
  }
  return null;
}

/** Grow an organic patch from a seed tile using random walk */
function growPatch(
  rng: SeededRandom, grid: TileType[][], cols: number, rows: number,
  seed: Coord, tileType: TileType, size: number,
): void {
  const dirs = [[0, -1], [1, 0], [0, 1], [-1, 0]];
  const frontier: Coord[] = [seed];
  grid[seed.row][seed.col] = tileType;
  let placed = 1;

  while (placed < size && frontier.length > 0) {
    const idx = rng.int(0, frontier.length - 1);
    const curr = frontier[idx];
    const shuffled = [...dirs];
    rng.shuffle(shuffled);

    let grew = false;
    for (const [dc, dr] of shuffled) {
      const nc = curr.col + dc, nr = curr.row + dr;
      if (nc < 1 || nc >= cols - 1 || nr < 1 || nr >= rows - 1) continue;
      if (grid[nr][nc] !== TileType.GROUND) continue;
      grid[nr][nc] = tileType;
      frontier.push({ col: nc, row: nr });
      placed++;
      grew = true;
      break;
    }
    if (!grew) frontier.splice(idx, 1); // dead end, remove from frontier
  }
}

// ── Fallback ──

function generateFallback(
  id: string, name: string, cols: number, rows: number,
  difficulty: DifficultyLabel, allowedEnemies: EnemyType[],
): MapDefinition {
  const grid = createEmptyGrid(cols, rows);
  placeBorders(grid, cols, rows);

  const midRow = Math.floor(rows / 2);
  grid[midRow][0] = TileType.SPAWN;
  grid[midRow][cols - 1] = TileType.BASE;
  // Zig-zag path instead of straight line
  let c = 1;
  let r = midRow;
  const path: Coord[] = [{ col: 0, row: midRow }];
  while (c < cols - 1) {
    grid[r][c] = TileType.PATH;
    path.push({ col: c, row: r });
    // Alternate: go right 2-3, then shift row
    if (c % 3 === 0 && r > 1 && r < rows - 2) {
      const shift = (c % 6 === 0) ? 1 : -1;
      r += shift;
      grid[r][c] = TileType.PATH;
      path.push({ col: c, row: r });
    }
    c++;
  }
  path.push({ col: cols - 1, row: midRow });

  // Placement above and below path
  for (let pr = 1; pr < rows - 1; pr++) {
    for (let pc = 1; pc < cols - 1; pc++) {
      if (grid[pr][pc] === TileType.GROUND && hasAdjacentType(grid, cols, rows, pc, pr, TileType.PATH)) {
        grid[pr][pc] = TileType.PLACEMENT;
      }
    }
  }

  const fallbackPaths = [simplifyPath(path)];
  const fallbackRng = new SeededRandom(id.split('').reduce((h, c) => ((h << 5) - h + c.charCodeAt(0)) | 0, 0));
  const tier = difficultyTier(difficulty);
  const decorations = placeDecorations(fallbackRng, grid, cols, rows, fallbackPaths, tier);

  return {
    id, name, difficulty,
    description: `${cols}x${rows} corridor`,
    cols, rows, grid,
    paths: fallbackPaths,
    allowedEnemies,
    decorations,
  };
}

// ── Decoration placement ──

// Natural / organic — open ground far from structures
const NATURE_DECOR = [
  DecorType.TREE, DecorType.DEAD_TREE, DecorType.SOUCHE,
  DecorType.ROCHER, DecorType.FLAQUE,
];

// Path-adjacent — industrial / roadside debris
const PATH_EDGE_DECOR = [
  DecorType.BARREL, DecorType.CAISSE, DecorType.CONDUIT,
  DecorType.ROCHER, DecorType.FLAQUE, DecorType.BARRIERE,
  DecorType.GARBAGE,
];

// Ruins / war-torn — appear from Moderate+ difficulty
const RUINS_DECOR = [
  DecorType.RUINS, DecorType.COLUMN, DecorType.BROKEN_WALL,
  DecorType.BROKEN_STATUE, DecorType.DEAD_TREE,
];

// Hazardous / toxic — appear from Hard+ difficulty
const HAZARD_DECOR = [
  DecorType.CONNARDIUM, DecorType.CHASM, DecorType.GARBAGE,
];

// Harvestable resources — scattered across the map, density scales with tier
const RESOURCE_DECOR: { type: DecorType; weight: number }[] = [
  { type: DecorType.CONNARDIUM, weight: 3 },  // Co
  { type: DecorType.GARBAGE,    weight: 3 },  // Electro
  { type: DecorType.RUINS,      weight: 2 },  // Co + Electro
  { type: DecorType.CAISSE,     weight: 4 },  // small Electro
  { type: DecorType.BARREL,     weight: 4 },  // small Co
];

/**
 * Scatter decorations across the map on eligible tiles.
 * Only on GROUND tiles — never on PATH, BASE, SPAWN, WATER, WALL, or PLACEMENT (Z).
 * Thematic pools vary by context (path-adjacent, open ground, ruins, hazards).
 * Pool variety and density scale with difficulty tier.
 */
export function placeDecorations(
  rng: SeededRandom, grid: TileType[][], cols: number, rows: number,
  paths: Coord[][], tier: number = 2,
): DecorDef[] {
  const decorations: DecorDef[] = [];
  const occupied = new Set<string>();

  // Build set of path-adjacent tiles for themed placement
  const pathTiles = new Set<string>();
  const pathAdjacentTiles = new Set<string>();
  for (const p of paths) {
    for (const wp of p) {
      pathTiles.add(`${wp.col},${wp.row}`);
      for (const [dc, dr] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) {
        const nc = wp.col + dc;
        const nr = wp.row + dr;
        if (nc >= 0 && nc < cols && nr >= 0 && nr < rows) {
          pathAdjacentTiles.add(`${nc},${nr}`);
        }
      }
    }
  }

  // Build set of wall-adjacent tiles for ruins placement
  const wallAdjacentTiles = new Set<string>();
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (grid[r][c] !== TileType.WALL) continue;
      for (const [dc, dr] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) {
        const nc = c + dc, nr = r + dr;
        if (nc >= 0 && nc < cols && nr >= 0 && nr < rows) {
          wallAdjacentTiles.add(`${nc},${nr}`);
        }
      }
    }
  }

  // ── Pass 1: Scatter resources across eligible ground tiles ──
  // Resource count scales with map area and difficulty
  const mapArea = cols * rows;
  const resourceCount = Math.floor(3 + mapArea * 0.012 + tier * 1.5);
  const totalWeight = RESOURCE_DECOR.reduce((s, r) => s + r.weight, 0);

  for (let i = 0; i < resourceCount; i++) {
    // Try random positions until we find a valid GROUND tile
    for (let attempt = 0; attempt < 40; attempt++) {
      const c = rng.int(1, cols - 2);
      const r = rng.int(1, rows - 2);
      const key = `${c},${r}`;
      if (grid[r][c] !== TileType.GROUND) continue;
      if (occupied.has(key)) continue;
      if (pathTiles.has(key)) continue;
      // Pick weighted random resource type
      let roll = rng.next() * totalWeight;
      let picked = RESOURCE_DECOR[0].type;
      for (const rd of RESOURCE_DECOR) {
        roll -= rd.weight;
        if (roll <= 0) { picked = rd.type; break; }
      }
      decorations.push({ type: picked, col: c, row: r });
      occupied.add(key);
      break;
    }
  }

  // ── Pass 2: Visual decorations (nature, ruins, hazards) ──
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const tile = grid[r][c];
      const key = `${c},${r}`;

      // Only place decorations on GROUND tiles
      if (tile !== TileType.GROUND) continue;
      if (occupied.has(key)) continue;

      const isPathAdj = pathAdjacentTiles.has(key) && !pathTiles.has(key);
      const isWallAdj = wallAdjacentTiles.has(key);

      let chance: number;
      let pool: DecorType[];

      if (isPathAdj) {
        // Roadside debris
        chance = 0.15;
        pool = PATH_EDGE_DECOR;
      } else if (isWallAdj && tier >= 3) {
        // Ruins near walls (Moderate+)
        chance = 0.18;
        pool = RUINS_DECOR;
      } else {
        // Open ground — nature
        chance = 0.12;
        pool = NATURE_DECOR;
      }

      // At higher difficulties, occasionally mix in hazard decor on open ground
      if (tier >= 5 && !isPathAdj && rng.next() < 0.3) {
        pool = HAZARD_DECOR;
      }

      if (rng.next() < chance) {
        const type = pool[rng.int(0, pool.length - 1)];
        decorations.push({ type, col: c, row: r });
        occupied.add(key);
      }
    }
  }

  return decorations;
}
